import { useEffect, useMemo, useRef, useState } from 'react';

import { type Root } from 'react-dom/client';
import { useHasFeatureFlag } from '@bringg-frontend/hooks';

import { ClusterOptions, ExtendedMarkerData, IdType, TooltipElement } from '../../types';
import usePrevious from '../../use-previous';
import { FIX_REALTIME_DRIVER_MARKER_LOCATION_FF } from '../consts';
import {
	addGoogleMarker,
	MARKER_REPOSITION_ANIMATION_DELAY,
	MARKER_REPOSITION_ANIMATION_DELTA,
	MARKER_REPOSITION_ANIMATION_DISTANCE_LIMIT,
	removeEntityFromMap,
	setMapCenter,
	shouldDisplayTooltipForSelectedMarker
} from '../utils/utils';
import { getDataForMarker, removeDataForMarker, setDataForMarker } from '../utils/markers-to-marker-data';
import { useClusters } from './use-clusters';
import { useMapTriggers } from './use-map-triggers';

type ReactRootContainer = TooltipElement & { __reactRoot?: Root };

type CancellableMarkerAnimation = { cancel: () => void };
type MarkerAnimationHandle = ReturnType<typeof setTimeout> | CancellableMarkerAnimation;

const isCancellableAnimation = (handle: MarkerAnimationHandle): handle is CancellableMarkerAnimation =>
	typeof (handle as CancellableMarkerAnimation).cancel === 'function';

const cancelMarkerAnimation = (handle: MarkerAnimationHandle | undefined) => {
	if (!handle) {
		return;
	}

	if (isCancellableAnimation(handle)) {
		handle.cancel();
	} else {
		clearTimeout(handle);
	}
};

// Coordinates can arrive as numbers, strings ("32.1") or google.maps.LatLng accessor functions
const toNumericCoord = (coord: unknown): number =>
	Number(typeof coord === 'function' ? (coord as () => number)() : coord ?? 0);

const disposeReactRootElement = (element: unknown) => {
	if (!(element instanceof HTMLElement)) {
		return;
	}
	const container = element as ReactRootContainer;
	container.dispose?.();
	container.__reactRoot?.unmount();
	delete container.__reactRoot;
};

export const useGoogleMarkers = ({
	markers,
	map,
	shouldAutoFocusMap,
	onSelectMarker,
	selectedMarkers,
	forceRecenterOnAddingMarkers,
	clusterOptions = { cluster: false }
}: {
	markers: ExtendedMarkerData[];
	map: google.maps.Map | null;
	shouldAutoFocusMap: boolean;
	onSelectMarker?: (isCtrlClick: boolean, markerId: IdType) => void;
	selectedMarkers?: IdType[];
	forceRecenterOnAddingMarkers?: boolean;
	clusterOptions?: ClusterOptions;
}) => {
	const unsubscribeFromMapEvents = useRef<google.maps.MapsEventListener[]>([]);
	const [markerInstanceByIdMap, setMarkersInstanceByIdMap] = useState<
		Map<IdType, google.maps.marker.AdvancedMarkerElement>
	>(new Map());
	const [timeoutByMarkerIdMap, setTimeoutByMarkerIdMap] = useState<Map<IdType, MarkerAnimationHandle>>(new Map());

	const fixMarkerRepositionAnimation = useHasFeatureFlag(FIX_REALTIME_DRIVER_MARKER_LOCATION_FF);

	const prevSelectedMarkers: IdType[] = usePrevious(selectedMarkers) || [];
	const prevMarkers: ExtendedMarkerData[] = usePrevious(markers) || [];

	const infoWindow = useMemo(() => {
		const window = new google.maps.InfoWindow({
			content: ''
		});

		unsubscribeFromMapEvents.current.push(
			window.addListener('visible', () => {
				const currentContent = window.getContent();
				(currentContent as TooltipElement)?.onTooltipOpen?.();
			})
		);

		return window;
	}, []);

	useEffect(() => {
		if (map) {
			unsubscribeFromMapEvents.current.push(
				google.maps.event.addListener(map, 'zoom_changed', () => infoWindow.close())
			);
		}
	}, [map]);

	useEffect(() => {
		infoWindow.close();
	}, [markers?.length]);

	useMapTriggers({
		map,
		markers,
		infoWindow,
		unsubscribeFromMapEvents
	});

	const { updateMarkerInCluster, markerClusterer } = useClusters({
		map,
		markerInstanceMap: markerInstanceByIdMap,
		infoWindow,
		clusterOptions
	});

	const removeMarker = (
		markerId: IdType,
		markerToRemove: google.maps.marker.AdvancedMarkerElement,
		timeoutsTemp?: Map<IdType, MarkerAnimationHandle>
	) => {
		// Clean up the event listener before removing the marker
		const markerData = getDataForMarker(markerToRemove);
		if (markerData?.markerTriggerListener) {
			markerData.markerTriggerListener.remove();
		}
		disposeReactRootElement(markerData?.tooltip);
		disposeReactRootElement(markerToRemove.content);
		removeEntityFromMap(markerToRemove);

		cancelMarkerAnimation(timeoutsTemp?.get(markerId));
	};

	const getMarkersToRedraw = (): IdType[] => {
		const selectedMarkersToRedraw = prevSelectedMarkers
			.filter(x => !(selectedMarkers || []).includes(x))
			.concat((selectedMarkers || []).filter(x => !prevSelectedMarkers.includes(x)));

		const dataMarkersToRedraw = markers
			.filter(marker => {
				const prevMarker = prevMarkers.find(m => m.id === marker.id);
				return (
					prevMarker &&
					(prevMarker.color !== marker.color ||
						prevMarker.text !== marker.text ||
						prevMarker.striped !== marker.striped ||
						prevMarker.opacity !== marker.opacity)
				);
			})
			.map(({ id }) => id);

		return [...selectedMarkersToRedraw, ...dataMarkersToRedraw];
	};

	const redrawSelectedMarkers = (markerInstanceMap: Map<IdType, google.maps.marker.AdvancedMarkerElement>) => {
		if (!map || (!markers && !selectedMarkers)) return;
		const markersToRedraw = getMarkersToRedraw();

		const infoWindowAnchor = (infoWindow as unknown as { anchor?: google.maps.marker.AdvancedMarkerElement })
			.anchor;
		const currentlyOpenMarkerId = infoWindowAnchor ? getDataForMarker(infoWindowAnchor)?.id : undefined;

		markersToRedraw?.forEach(markerId => {
			const markerToRemove = markerInstanceByIdMap.get(markerId);
			if (markerToRemove) {
				removeMarker(markerId, markerToRemove);
				removeDataForMarker(markerToRemove);
			}

			const markerIndex = markers.findIndex(m => m.id === markerId);
			const markerData = { ...markers[markerIndex] };
			const googleMarker = addGoogleMarker({
				map,
				markerData: markerData,
				infoWindow,
				onSelectMarker,
				selectedMarkers,
				hideAnimation: true,
				shouldOpenTooltipOnCreation:
					currentlyOpenMarkerId === markerId &&
					shouldDisplayTooltipForSelectedMarker(selectedMarkers, markerId)
			});
			// @ts-ignore — markers is protected but accessible at runtime
			const isTracked = markerClusterer?.markers?.includes(googleMarker);

			if (googleMarker) {
				if (!isTracked) {
					// Prevent flash: hide immediately if this marker will be managed by the
					// clusterer. The clusterer controls its visibility when it re-renders.
					if (clusterOptions.cluster && markerData.shouldCluster) {
						googleMarker.map = null;
					}
					markerInstanceMap.set(markerId, googleMarker);
				} else {
					markerInstanceMap.delete(markerId);
				}
			}
		});
	};

	const isMarkersMapsListDifferent = (newMarkersMap: Map<IdType, google.maps.marker.AdvancedMarkerElement>) => {
		if (newMarkersMap.size !== markerInstanceByIdMap.size) {
			return true;
		}

		for (const [key, value] of newMarkersMap) {
			if (!markerInstanceByIdMap.has(key) || markerInstanceByIdMap.get(key) !== value) {
				return true;
			}
		}

		return false;
	};

	useEffect(() => {
		if (!map || (!markers && !markerInstanceByIdMap.size)) return;

		const temp = new Map(markerInstanceByIdMap);
		const timeoutsTemp = new Map(timeoutByMarkerIdMap);
		const markerIdsToRemove = new Set(temp.keys());

		if (markers?.length) {
			markers.forEach((m: ExtendedMarkerData) => {
				if (!m) return;

				let markerInstance = temp.get(m.id);
				if (!markerInstance) {
					markerInstance = addGoogleMarker({
						map,
						markerData: m,
						infoWindow,
						onSelectMarker,
						selectedMarkers
					});
					if (!markerInstance) {
						return;
					}

					// Prevent flash: hide immediately if this marker will be managed by the
					// clusterer. The clusterer controls its visibility when it re-renders.
					// Without this, the marker is visible for one or more frames between
					// addGoogleMarker and the useClusters effect that rebuilds the cluster.
					if (clusterOptions.cluster && m.shouldCluster) {
						markerInstance.map = null;
					}

					temp.set(m.id, markerInstance);
					return;
				}

				setDataForMarker(markerInstance, m);

				markerIdsToRemove.delete(m.id);

				const targetLat = Number(m.location.lat);
				const targetLng = Number(m.location.lng);
				const isTargetValid = !Number.isNaN(targetLat) && !Number.isNaN(targetLng);

				// The server can deliver lat/lng as strings — strict comparison treats "32.1" !== 32.1
				// as a position change and spawns redundant animations, so compare numerically
				const hasPositionChanged = fixMarkerRepositionAnimation
					? isTargetValid &&
					  (toNumericCoord(markerInstance.position?.lat) !== targetLat ||
							toNumericCoord(markerInstance.position?.lng) !== targetLng)
					: markerInstance.position?.lat !== m.location.lat ||
					  markerInstance.position?.lng !== m.location.lng;

				if (hasPositionChanged) {
					const moveToLatLng = fixMarkerRepositionAnimation
						? new google.maps.LatLng(targetLat, targetLng)
						: new google.maps.LatLng(m.location.lat, m.location.lng);
					const markerPosition = markerInstance?.position;

					if (
						markerPosition &&
						isMoveToLocationTooFar(markerPosition as google.maps.LatLngAltitude, moveToLatLng)
					) {
						cancelMarkerAnimation(timeoutsTemp.get(m.id));
						markerInstance.position = moveToLatLng;
						if (shouldAutoFocusMap && m.shouldAutoFocus) {
							setMapCenter(map, m.location);
						}
						updateMarkerInCluster(markerInstance);
					} else {
						if (fixMarkerRepositionAnimation) {
							// Overlapping animations compound their deltas and overshoot the target,
							// so cancel the in-flight animation before starting a new one
							cancelMarkerAnimation(timeoutsTemp.get(m.id));
						}

						const markerAnimation = fixMarkerRepositionAnimation
							? runInterpolatedMoveMarkerAnimation(markerInstance, m, map, shouldAutoFocusMap, () =>
									updateMarkerInCluster(markerInstance)
							  )
							: runMoveMarkerAnimation(markerInstance, m, map, shouldAutoFocusMap, () =>
									updateMarkerInCluster(markerInstance)
							  );
						markerAnimation && timeoutsTemp.set(m.id, markerAnimation);
					}
				}
			});
		}

		markerIdsToRemove.forEach(id => {
			const markerToRemove = temp.get(id);
			markerToRemove && removeMarker(id, markerToRemove, timeoutsTemp);
			temp.delete(id);
		});

		redrawSelectedMarkers(temp);

		if (isMarkersMapsListDifferent(temp)) {
			setMarkersInstanceByIdMap(temp);
		}
		setTimeoutByMarkerIdMap(timeoutsTemp);

		return () => timeoutByMarkerIdMap.forEach(value => cancelMarkerAnimation(value));
	}, [map, markers, shouldAutoFocusMap, selectedMarkers, fixMarkerRepositionAnimation]);

	useEffect(() => {
		if (map && forceRecenterOnAddingMarkers && !shouldAutoFocusMap) {
			markers.forEach(m => {
				if (!m) {
					return;
				}
				setMapCenter(map, m.location);
			});
		}
	}, [markers?.length]);

	useEffect(() => {
		return () => {
			unsubscribeFromMapEvents.current.forEach(listener => {
				listener?.remove();
			});
			unsubscribeFromMapEvents.current = [];
		};
	}, []);

	return markerInstanceByIdMap;
};

const runMoveMarkerAnimation = (
	marker: google.maps.marker.AdvancedMarkerElement,
	markerData: ExtendedMarkerData,
	map: google.maps.Map,
	shouldAutoFocusMap: boolean,
	onAnimationComplete?: () => void
) => {
	let timeoutInstance;
	const current = marker.position;
	const moveTo = new google.maps.LatLng(markerData.location.lat, markerData.location.lng);

	const deltalat =
		(moveTo.lat() - (typeof current?.lat === 'function' ? current.lat() : current?.lat || 0)) /
		MARKER_REPOSITION_ANIMATION_DELTA;
	const deltalng =
		(moveTo.lng() - (typeof current?.lng === 'function' ? current.lng() : current?.lng || 0)) /
		MARKER_REPOSITION_ANIMATION_DELTA;

	const isLastStep = (stepIndex: number) => stepIndex === MARKER_REPOSITION_ANIMATION_DELTA - 1;

	for (let i = 0; i < MARKER_REPOSITION_ANIMATION_DELTA; i++) {
		timeoutInstance = setTimeout(() => {
			let lat = typeof marker.position?.lat === 'function' ? marker.position.lat() : marker.position?.lat || 0;
			let lng = typeof marker.position?.lng === 'function' ? marker.position.lng() : marker.position?.lng || 0;
			lat += deltalat;
			lng += deltalng;
			const latlng = new google.maps.LatLng(lat, lng);
			marker.position = latlng;
			if (shouldAutoFocusMap && markerData.shouldAutoFocus) {
				setMapCenter(map, latlng);
			}

			if (isLastStep(i)) {
				onAnimationComplete?.();
			}
		}, MARKER_REPOSITION_ANIMATION_DELAY * i);
	}

	return timeoutInstance;
};

// Cancellable replacement for runMoveMarkerAnimation (behind fix_realtime_driver_marker_location):
// every step positions the marker at an absolute interpolation from the captured start,
// so overshoot is impossible even if a stray step from an un-canceled animation overlaps
const runInterpolatedMoveMarkerAnimation = (
	marker: google.maps.marker.AdvancedMarkerElement,
	markerData: ExtendedMarkerData,
	map: google.maps.Map,
	shouldAutoFocusMap: boolean,
	onAnimationComplete?: () => void
): CancellableMarkerAnimation | undefined => {
	const startLat = toNumericCoord(marker.position?.lat);
	const startLng = toNumericCoord(marker.position?.lng);
	const targetLat = Number(markerData.location.lat);
	const targetLng = Number(markerData.location.lng);

	// Never animate from or toward a non-numeric coordinate — keep the last valid position
	if ([startLat, startLng, targetLat, targetLng].some(Number.isNaN)) {
		return undefined;
	}

	const timeouts: ReturnType<typeof setTimeout>[] = [];

	for (let i = 1; i <= MARKER_REPOSITION_ANIMATION_DELTA; i++) {
		const progress = i / MARKER_REPOSITION_ANIMATION_DELTA;
		const isLastStep = i === MARKER_REPOSITION_ANIMATION_DELTA;

		timeouts.push(
			setTimeout(() => {
				const latlng = isLastStep
					? new google.maps.LatLng(targetLat, targetLng)
					: new google.maps.LatLng(
							startLat + progress * (targetLat - startLat),
							startLng + progress * (targetLng - startLng)
					  );
				marker.position = latlng;
				if (shouldAutoFocusMap && markerData.shouldAutoFocus) {
					setMapCenter(map, latlng);
				}

				if (isLastStep) {
					onAnimationComplete?.();
				}
			}, MARKER_REPOSITION_ANIMATION_DELAY * i)
		);
	}

	return { cancel: () => timeouts.forEach(timeout => clearTimeout(timeout)) };
};

const isMoveToLocationTooFar = (current: google.maps.LatLngAltitude, moveTo: google.maps.LatLng) => {
	const latSorted = [moveTo.lat(), current.lat].sort((a, b) => b - a);
	const lngSorted = [moveTo.lng(), current.lng].sort((a, b) => b - a);

	const latSum = latSorted[0] - latSorted[1];
	const lngSum = lngSorted[0] - lngSorted[1];

	const distanceSum = latSum + lngSum;
	return distanceSum > MARKER_REPOSITION_ANIMATION_DISTANCE_LIMIT;
};
